/**
 * 图层与 GeoJSON 数据构造 —— 纯函数，不碰 MapLibre。
 *
 * ## 为什么数据构造单独一层
 *
 * "位置为 `null` 时不能画在 (0,0)"这条要求，只有在**数据**这一层才可验证：
 * 一旦进了图层，空的 `FeatureCollection` 和装在 (0,0) 的 `Point` 在界面上
 * 是同一张图（都"没有东西"）或者差一个大陆。所以所有 `…Collection()` 函数
 * 都是纯的，测试直接断言"它返回了 0 个要素"。
 *
 * ## 为什么没有 symbol（文字/图标）图层
 *
 * `symbol` 图层需要 glyphs/精灵图，而它们要从网络取。一个断网就要能用的地图
 * 不能依赖任何"必须联网才渲染得出来"的东西 —— 所以标注一律用 `circle`/`line`
 * 这类内置几何画，代价是没法写文字，收益是离线时**图标一定在**。
 *
 * ## 配色
 *
 * 颜色只能来自 `@hgc/ui` 的 `--map-*` 令牌（见 `palette.ts`）：MapLibre 的
 * paint 值是运行时字符串，写不进 Tailwind 类名，所以这里把同一批令牌读出来用。
 */

import {
  destinationPoint,
  polylineLengthM,
  type GeoBounds,
  type LatLon,
} from '@hgc/ardupilot';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import type { TileCoord } from './tiles';
import { tileBounds } from './tiles';

/* ───────────────────────── 类型 ───────────────────────── */

export type MapLayerType = 'background' | 'raster' | 'line' | 'circle' | 'fill';

export interface MapLayerSpec {
  readonly id: string;
  readonly type: MapLayerType;
  /** 关联的数据源 id；`background` 图层没有 */
  readonly source?: string;
  readonly paint?: Readonly<Record<string, unknown>>;
  readonly layout?: Readonly<Record<string, unknown>>;
  /** 图层可见的最粗/最细缩放层 —— 太密的图层在远景下只会变成噪点 */
  readonly minzoom?: number;
  readonly maxzoom?: number;
}

export type PointCollection = FeatureCollection<Point, Record<string, unknown>>;
export type LineCollection = FeatureCollection<LineString, Record<string, unknown>>;
export type PolygonCollection = FeatureCollection<Polygon, Record<string, unknown>>;

export function emptyCollection<G extends Point | LineString | Polygon>(): FeatureCollection<
  G,
  Record<string, unknown>
> {
  return { type: 'FeatureCollection', features: [] };
}

/* ───────────────────────── 固定 id ───────────────────────── */

/**
 * 源 id 与图层 id 全部集中在这里。
 *
 * 集中管理的实际收益：`setStyle` 换底图后要**重新挂载**这些图层，
 * 而"哪些 id 是我们自己的"必须是可枚举的（否则只能靠字符串前缀猜，
 * 一不小心就把用户样式里的图层删了）。
 */
export const MAP_SOURCE_IDS = {
  basemap: 'hgc-basemap',
  vehicle: 'hgc-vehicle',
  trail: 'hgc-trail',
  home: 'hgc-home',
  target: 'hgc-target',
  coverage: 'hgc-coverage',
} as const;

export const MAP_LAYER_IDS = {
  basemapBackground: 'hgc-basemap-background',
  basemapRaster: 'hgc-basemap-raster',
  coverageMissing: 'hgc-coverage-missing',
  trail: 'hgc-trail-line',
  vehicleHeading: 'hgc-vehicle-heading',
  vehicleDot: 'hgc-vehicle-dot',
  home: 'hgc-home-dot',
  target: 'hgc-target-dot',
} as const;

/**
 * 绘制/规划层（`map-draw`、`plan-map-editing`）的接入约定。
 *
 * 它们不走 `MAP_LAYER_IDS`：引擎提供 `addOverlay()`，调用方自带 id 与图层描述，
 * 并且这些图层同样会在换底图后自动重新挂载。命名约定 `hgc-<功能>-…`
 * 只用于人眼排查（日志里一眼能看出是谁的图层）。
 */
export const OVERLAY_ID_PREFIX = 'hgc-';

/* ───────────────────────── 要素构造 ───────────────────────── */

function pointFeature(point: LatLon, properties: Record<string, unknown>): Feature<Point, Record<string, unknown>> {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [point.lonDeg, point.latDeg] },
    properties,
  };
}

/**
 * 机体图层的数据：一个点 + 一条航向线。
 *
 * `position === null` → **空集合**。这是"几内亚湾 bug"的最后一道闸：
 * 上游任何一层把 `null` 换成 `0`，这里都会画出一个位于 (0,0) 的图标，
 * 所以这一层宁可什么都不画 —— 用户在界面上看到"没有图标"，
 * 会去看状态提示（"GPS 尚未定位"），而不是误以为机体在非洲西海岸。
 */
export function vehicleCollection(
  position: LatLon | null,
  options: { readonly headingDeg?: number | null; readonly headingLengthM?: number } = {},
): FeatureCollection<Point | LineString, Record<string, unknown>> {
  if (position === null) return emptyCollection<Point | LineString>();
  const features: Feature<Point | LineString, Record<string, unknown>>[] = [
    pointFeature(position, {}),
  ];

  const heading = options.headingDeg;
  const lengthM = options.headingLengthM ?? 0;
  if (heading !== null && heading !== undefined && Number.isFinite(heading) && lengthM > 0) {
    const tip = destinationPoint(position, heading, lengthM);
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [position.lonDeg, position.latDeg],
          [tip.lonDeg, tip.latDeg],
        ],
      },
      properties: { headingDeg: heading },
    });
  }

  return { type: 'FeatureCollection', features };
}

/** 航迹线。少于 2 个点时不画（单点线在地图上什么都看不到，只是一个无意义的请求）。 */
export function trailCollection(points: readonly LatLon[]): LineCollection {
  if (points.length < 2) return emptyCollection<LineString>();
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: points.map((point) => [point.lonDeg, point.latDeg]),
        },
        properties: {},
      },
    ],
  };
}

export function homeCollection(home: LatLon | null): PointCollection {
  return home === null ? emptyCollection<Point>() : { type: 'FeatureCollection', features: [pointFeature(home, {})] };
}

export function targetCollection(target: LatLon | null): PointCollection {
  return target === null
    ? emptyCollection<Point>()
    : { type: 'FeatureCollection', features: [pointFeature(target, {})] };
}

/**
 * 缺失瓦片 → 面。
 *
 * 这是"这块区域没有缓存"的**图形化答复**：文字清单能说出有几块，
 * 而一块块灰色方块直接告诉用户"往哪个方向飞就有图了"。
 */
export function missingTilesCollection(tiles: readonly TileCoord[]): PolygonCollection {
  const features: Feature<Polygon, Record<string, unknown>>[] = [];
  for (const tile of tiles) {
    const bounds = tileBounds(tile);
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [bounds.westDeg, bounds.southDeg],
            [bounds.eastDeg, bounds.southDeg],
            [bounds.eastDeg, bounds.northDeg],
            [bounds.westDeg, bounds.northDeg],
            [bounds.westDeg, bounds.southDeg],
          ],
        ],
      },
      properties: { z: tile.z, x: tile.x, y: tile.y },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** 缓冲盒 → 面（"全览航迹"之外还可以用来高亮某个作业区）。 */
export function boundsCollection(bounds: GeoBounds): PolygonCollection {
  const west = bounds.westDeg;
  const east = bounds.crossesAntimeridian ? bounds.eastDeg + 360 : bounds.eastDeg;
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [west, bounds.southDeg],
              [east, bounds.southDeg],
              [east, bounds.northDeg],
              [west, bounds.northDeg],
              [west, bounds.southDeg],
            ],
          ],
        },
        properties: {},
      },
    ],
  };
}

/**
 * 航向线的长度（米）。
 *
 * 需要它是因为**米不是视觉单位**：固定 100 m 的线在 19 层是几百像素的长矛，
 * 在 10 层却看不见。这里按缩放换算，让它在屏幕上始终约等于 `pixels` 像素 ——
 * 也就是"与缩放无关的视觉长度"。经度方向还要乘 `cos(纬度)`：
 * 墨卡托在高纬度把东西向拉长，不修正的话北纬 60° 的航向线会短一半。
 */
export function headingLengthM(zoom: number, latDeg: number, pixels = 22): number {
  const metersPerPixel = (156543.03392 * Math.cos((clampLat(latDeg) * Math.PI) / 180)) / 2 ** clampZoom(zoom);
  return Math.max(1, metersPerPixel * pixels);
}

function clampLat(latDeg: number): number {
  return latDeg < -85 ? -85 : latDeg > 85 ? 85 : latDeg;
}

function clampZoom(zoom: number): number {
  return zoom < 0 ? 0 : zoom > 22 ? 22 : zoom;
}

/* ───────────────────────── 图层描述 ───────────────────────── */

/** 与 `@hgc/ui` 的 `--map-*` 令牌一一对应（见 `palette.ts`）。 */
export interface MapPalette {
  readonly vehicle: string;
  readonly trail: string;
  readonly home: string;
  readonly target: string;
  readonly mission: string;
  readonly missing: string;
  /** 标记描边色：取页面背景色，保证在深浅两种底图上都能看出轮廓 */
  readonly markerOutline: string;
  readonly background: string;
}

/** 机体/返航点/目标点的屏幕尺寸随缩放插值 —— 远景下不糊成一团，近景下不遮住地形。 */
const MARKER_RADIUS_ZOOM_STOPS: readonly unknown[] = ['interpolate', ['linear'], ['zoom'], 6, 4, 12];

export function vehicleLayerSpecs(palette: MapPalette): readonly MapLayerSpec[] {
  return [
    {
      id: MAP_LAYER_IDS.vehicleHeading,
      type: 'line',
      source: MAP_SOURCE_IDS.vehicle,
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': palette.vehicle,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 14, 3],
        'line-opacity': 0.85,
      },
      minzoom: 12,
    },
    {
      id: MAP_LAYER_IDS.vehicleDot,
      type: 'circle',
      source: MAP_SOURCE_IDS.vehicle,
      paint: {
        'circle-color': palette.vehicle,
        'circle-radius': [...MARKER_RADIUS_ZOOM_STOPS, 7, 18, 11],
        'circle-stroke-color': palette.markerOutline,
        'circle-stroke-width': 2,
      },
    },
  ];
}

export function trailLayerSpecs(palette: MapPalette): readonly MapLayerSpec[] {
  return [
    {
      id: MAP_LAYER_IDS.trail,
      type: 'line',
      source: MAP_SOURCE_IDS.trail,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': palette.trail,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 14, 2.5],
        'line-opacity': 0.85,
      },
    },
  ];
}

export function homeLayerSpecs(palette: MapPalette): readonly MapLayerSpec[] {
  return [
    {
      id: MAP_LAYER_IDS.home,
      type: 'circle',
      source: MAP_SOURCE_IDS.home,
      paint: {
        'circle-color': palette.home,
        'circle-radius': [...MARKER_RADIUS_ZOOM_STOPS, 5.5, 18, 8],
        'circle-stroke-color': palette.markerOutline,
        'circle-stroke-width': 2,
      },
    },
  ];
}

export function targetLayerSpecs(palette: MapPalette): readonly MapLayerSpec[] {
  return [
    {
      id: MAP_LAYER_IDS.target,
      type: 'circle',
      source: MAP_SOURCE_IDS.target,
      paint: {
        'circle-color': palette.target,
        'circle-radius': [...MARKER_RADIUS_ZOOM_STOPS, 6, 18, 9],
        'circle-stroke-color': palette.markerOutline,
        'circle-stroke-width': 2,
      },
    },
  ];
}

/** 缺失瓦片覆盖层：半透明面 + 明显描边，压在航迹下面、底图上面。 */
export function missingTilesLayerSpecs(palette: MapPalette): readonly MapLayerSpec[] {
  return [
    {
      id: MAP_LAYER_IDS.coverageMissing,
      type: 'fill',
      source: MAP_SOURCE_IDS.coverage,
      paint: {
        'fill-color': palette.missing,
        'fill-opacity': 0.28,
        'fill-outline-color': palette.missing,
      },
    },
  ];
}

/**
 * 航迹的像素长度（用于"全览"时留白）—— 直接给出包围盒与 padding，
 * 由引擎调用 `fitBounds`。这里是纯计算，方便测试"少于两个点时不缩到零"。
 */
export function overviewPaddingRatio(pointCount: number): number {
  // 航迹很长时留白要小一点，否则视野被 padding 挤掉、看不出轨迹走向
  if (pointCount < 10) return 0.28;
  if (pointCount < 200) return 0.16;
  return 0.08;
}

/** 航迹的几何长度（米），界面用它显示"航迹 12.3 km"。 */
export function trailLengthForDisplayM(points: readonly LatLon[]): number {
  return polylineLengthM(points);
}
